import React, { PureComponent } from "react";
import { mdiMenuDown, mdiMenuUp, mdiTrendingUp, mdiLock } from "@mdi/js";
import Icon from "@mdi/react";
import DetectableOverflow from "react-detectable-overflow";
import { BetQLSportItem, NameKeyPair } from "src/common-services";

import styles from "./styles.scss";
import { renderRatingByValue } from "../../../../services/ValueDataService";
import { SubscriptionContext } from "../../../../services/SubscriptionService";
import { Button } from "../../../Button";
import ButtonGroup, { ButtonGroupItem } from "../../../ButtonGroup";
import { getFreeTrialClaimed } from "src/services/UserService";

/**
 * Interface to sort the trends in specific manner
 */
interface TrendSorter {
  name: string;
  sort: (direction: number, trends: Array<any>) => Array<any>;
}

/**
 * State representation of trending summary
 */
interface State {
  hasOverflow: boolean;
  showAllTrends: boolean;
  selectedFilterIndex: number;
  trendSorter: TrendSorter;
  sortDirection: number;
}

/**
 * Data passed onto the trending summary
 */
interface Props {
  freeGame: boolean;
  gameId: string;
  awayTeam: any;
  homeTeam: any;
  trends: Array<any>;
  currentSportObj: BetQLSportItem;
  onTrendSelected: (selectedTrend: NameKeyPair) => void;
  currentTrendType: NameKeyPair;
  trendButtonItemList: ButtonGroupItem[];
  teamSelected: string;
}

const RATING_KEY: string = "rating";
const GAMES_KEY: string = "games";
const RECORD_KEY: string = "record";
const ROI_KEY: string = "roi";
const UNITS_WON_KEY: string = "unitsWon";

/**
 * Sort the trends by rating
 */
const RATING_TREND_SORTER: TrendSorter = {
  name: RATING_KEY,
  sort: (direction: number, trendsCopy: Array<any>) => {
    trendsCopy.sort((item1, item2) => {
      return (Math.abs(item2.rating) - Math.abs(item1.rating)) * direction;
    });
    return trendsCopy;
  },
};

/**
 * Sort by total number of games
 */
const GAMES_TREND_SORTER: TrendSorter = {
  name: GAMES_KEY,
  sort: (direction: number, trendsCopy: Array<any>) => {
    trendsCopy.sort((item1, item2) => {
      return (item2.games - item1.games) * direction;
    });
    return trendsCopy;
  },
};

/**
 * Sort by win % percentage
 */
const RECORD_TREND_SORTER: TrendSorter = {
  name: RECORD_KEY,
  sort: (direction: number, trendsCopy: Array<any>) => {
    trendsCopy.sort((item1, item2) => {
      const winPercentage1: number = item1.wins / item1.games;
      const winPercentage2: number = item2.wins / item2.games;
      return (winPercentage2 - winPercentage1) * direction;
    });
    return trendsCopy;
  },
};

/**
 * Sort by ROI - defined by number of units won / games
 */
const ROI_TREND_SORTER: TrendSorter = {
  name: ROI_KEY,
  sort: (direction: number, trendsCopy: Array<any>) => {
    trendsCopy.sort((item1, item2) => {
      const roi1: number = Math.abs(item1.units / item1.games) * 100.0;
      const roi2: number = Math.abs(item2.units / item2.games) * 100.0;
      return (roi2 - roi1) * direction;
    });
    return trendsCopy;
  },
};

/**
 * Sort by units won
 */
const UNITS_TREND_SORTER: TrendSorter = {
  name: UNITS_WON_KEY,
  sort: (direction: number, trendsCopy: Array<any>) => {
    trendsCopy.sort((item1, item2) => {
      return (item2.units - item1.units) * direction;
    });
    return trendsCopy;
  },
};

/**
 * Trend sorter lookup map
 */
interface SorterLookup {
  [key: string]: TrendSorter;
}

/**
/**
 * Sorter lookups
 */
const SORTER_LOOKUP: SorterLookup = {
  rating: RATING_TREND_SORTER,
  games: GAMES_TREND_SORTER,
  record: RECORD_TREND_SORTER,
  roi: ROI_TREND_SORTER,
  unitsWon: UNITS_TREND_SORTER,
};

/**
 * Trending summary provides overview of trending data in a concise manner.
 */
class TrendingList extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      hasOverflow: false,
      showAllTrends: false,
      selectedFilterIndex: 0,
      trendSorter: RATING_TREND_SORTER,
      sortDirection: 1,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.currentTrendType.key !== prevProps.currentTrendType.key) {
      const selectedFilterIndex: number = this.props.trendButtonItemList.findIndex((item) => {
        return item.key === this.props.currentTrendType.key;
      });

      this.setState({ selectedFilterIndex });
    }
  }

  /**
   * Render list of table rows
   *
   * @param trendList Array of schedule
   */
  _renderTrendList = (trendList: Array<any>) => {
    return trendList.length > 0 ? (
      trendList.map((trend: any, index: number) => {
        return (
          <React.Fragment key={index}>
            <span className="trend-list__table-cell trend-list__table-cell-rating">
              {renderRatingByValue(trend.rating, false)}
            </span>
            <span className="trend-list__table-cell trend-list__table-cell-description">
              <span className="trend-list__trend-report-type">{trend.reportType}</span>
              {trend.description}
            </span>

            <span className="trend-list__table-cell">
              <span className="trend-list__table-column-mobile">Games</span>
              <span className="trend-list__table-data">{trend.games}</span>
            </span>

            <span className="trend-list__table-cell">
              <span className="trend-list__table-column-mobile">Record</span>
              <span className="trend-list__table-data">
                {trend.rating > 0 ? `${trend.wins}-${trend.losses}` : `${trend.losses}-${trend.wins}`}
              </span>
            </span>

            <span className="trend-list__row-divider" />
          </React.Fragment>
        );
      })
    ) : (
      <Icon path={mdiTrendingUp} size="24px" color="#FFCC01" />
    );
  };

  /**
   * Helper method to apply filtering to this schedule list
   *
   * @param item Filter item, see beginning of this file
   * @param index The button index of the selection
   */
  onFilterUpdate = (item: NameKeyPair, selectedFilterIndex: number) => {
    this.setState({ selectedFilterIndex });
    this.props.onTrendSelected(item);
  };

  /**
   * Display the sorting caret which indicate the direction of the sort.
   *
   * @param direction 1 = desc, -1 asc
   */
  _renderSortCaret = (direction: number) => {
    if (direction === 0) {
      return null;
    }

    return <Icon path={direction > 0 ? mdiMenuDown : mdiMenuUp} size="16px" color="#888888" />;
  };

  /**
   * Update the sorter and change the sort caret direction if needed
   *
   * @param columnKey New Column Key
   */
  _onSelectColumnSort = (columnKey: string) => {
    const { trendSorter, sortDirection } = this.state;
    const nextSorter: TrendSorter = SORTER_LOOKUP[columnKey];
    if (trendSorter.name === nextSorter.name) {
      // 0 -> 1 -> -1
      let nextSortDirection: number = sortDirection;
      if (sortDirection === -1) {
        nextSortDirection = 0;
      } else if (sortDirection === 1) {
        nextSortDirection = -1;
      } else {
        nextSortDirection = 1;
      }

      this.setState({ sortDirection: nextSortDirection });
    } else {
      this.setState({ trendSorter: nextSorter, sortDirection: 1 });
    }
  };

  /**
   * Render the sortable column by given column key/label
   *
   * @param columnKey Column key for this sortable column
   * @param columnLabel The display text of the column
   */
  _renderSortableColumn = (columnKey: string, columnLabel: string) => {
    const { trendSorter, sortDirection } = this.state;

    return (
      <span
        className="trend-list__table-column trend-list__table-column-sortable"
        onClick={() => {
          this._onSelectColumnSort(columnKey);
        }}
      >
        {columnLabel}
        {trendSorter.name === columnKey ? this._renderSortCaret(sortDirection) : null}
      </span>
    );
  };

  _toggleShowMoreLess = () => {
    const showAllTrends: any = this.state.showAllTrends;
    this.setState({ showAllTrends: !showAllTrends });
  };

  render() {
    const { selectedFilterIndex, showAllTrends, hasOverflow, trendSorter, sortDirection } = this.state;
    const { trends, currentSportObj, awayTeam, homeTeam, gameId, freeGame } = this.props;
    const filteredTrendList = trendSorter.sort(sortDirection, trends);
    const currentTrend: NameKeyPair = currentSportObj.trendTypes[selectedFilterIndex];

    return (
      <SubscriptionContext.Consumer>
        {({ hasSubscriptionAccess, redirectToPricing }) => {
          const hasAccess = hasSubscriptionAccess(this.props.currentSportObj.key);
          return (
            <div className="trend-list__container">
              <div className="trend-list__title-container">
                <div className="trend-list__title">Trends Overview</div>
                {hasAccess ? (
                  <React.Fragment>
                    <div className="trend-list__filter-section">
                      <ButtonGroup
                        selectedIndex={selectedFilterIndex}
                        equalWidth={true}
                        onSelectItem={this.onFilterUpdate}
                        items={this.props.trendButtonItemList}
                      />
                    </div>
                    <div className="trend-list__filter-section-xs">
                      <ButtonGroup
                        inverseSelectedButton={true}
                        selectedIndex={selectedFilterIndex}
                        equalWidth={true}
                        onSelectItem={this.onFilterUpdate}
                        items={this.props.trendButtonItemList}
                      />
                    </div>
                  </React.Fragment>
                ) : null}
              </div>
              {freeGame || hasAccess ? (
                <div>
                  <DetectableOverflow
                    className={`trend-list__table-container ${
                      showAllTrends ? "trend-list__table-container-opened" : "trend-list__table-container-closed"
                    }`}
                    onChange={(overflow: boolean) => this.setState({ hasOverflow: overflow })}
                  >
                    {filteredTrendList.length > 0 ? (
                      <div className="trend-list__table">
                        {this._renderSortableColumn("rating", "Stars")}
                        <span className="trend-list__table-column">Top Trend & Type</span>
                        {this._renderSortableColumn("games", "Total Games")}
                        {this._renderSortableColumn("record", "Record")}
                        {this._renderSortableColumn("", "")}
                        {this._renderSortableColumn("", "")}
                        {this._renderTrendList(filteredTrendList)}
                      </div>
                    ) : (
                      <div className="trend-list__no-trending-container">
                        <Icon path={mdiTrendingUp} size="48px" color="#000000" />
                        <div className="trend-list__no-trending-text">
                          There are no favorable {currentTrend.name} trends for {this.props.teamSelected} in this game
                        </div>
                      </div>
                    )}
                  </DetectableOverflow>
                  {hasOverflow || showAllTrends ? (
                    <div className="trend-list__show-more-less" onClick={this._toggleShowMoreLess}>
                      {showAllTrends ? "See Less" : "See All Trends"}
                    </div>
                  ) : null}
                </div>
              ) : (
                <Button
                  className="trend-list__upgrade-button"
                  onClick={() =>
                    redirectToPricing(
                      {
                        screen: "GameView",
                        page: "Game Detail",
                        sport: currentSportObj.name,
                        source: "Trend List",
                        awayTeam: awayTeam.fullName,
                        homeTeam: homeTeam.fullName,
                        gameId: gameId,
                      },
                      "Game Detail Trend List"
                    )
                  }
                >
                  <Icon
                    style={{ marginRight: "5px", verticalAlign: "middle" }}
                    path={mdiLock}
                    size="14px"
                    color="#fff"
                  />
                  <span>{getFreeTrialClaimed() ? "Upgrade now" : "Start Free Trial"}</span>
                </Button>
              )}
              <style jsx global>
                {styles}
              </style>
            </div>
          );
        }}
      </SubscriptionContext.Consumer>
    );
  }
}

export default TrendingList;
